import React, { useState } from 'react';

import { noop } from 'lodash';
import { useNavigate } from 'react-router-dom';

import ClaimInfoPanel from './ClaimInfoPanel';
import UserMenu from './UserMenu';

import { BreadcrumbItem } from '~/components/MainTopBar/BreadcrumbItem';
import { IAuthUser } from '~/context/AuthContext';
import IconButton from '~/core/atoms/Buttons/IconButton';
import TextButton from '~/core/atoms/Buttons/TextButton';
import { LINE_OF_BUSINESS, LINE_OF_BUSINESS_ICON_MAP } from '~/core/constants/LineOfBusiness';
import CloseIcon from '~/core/iconComponents/icons/CloseIcon';
import HamburgerMenuIcon from '~/core/iconComponents/icons/HamburgerMenuIcon';
import SideMenuIcon from '~/core/iconComponents/icons/SideMenuIcon';
import CliveAvatar from '~/core/iconComponents/special/CliveAvatar';
import cn from '~/utils/cn';

interface ClaimInfoItem {
  label: string;
  value?: string;
}

interface BreadcrumbItem {
  label: string;
  action?: () => void;
}

interface MainTopBarProps {
  claimId?: string;
  onToggleSidebar?: () => void;
  onToggleMenu?: () => void;
  user: IAuthUser | null;
  lineOfBusiness?: LINE_OF_BUSINESS;
  claimInfo?: Record<string, ClaimInfoItem>;
  onLogout?: () => void;
  breadcrumbItem?: BreadcrumbItem;
}

const MainTopBar: React.FC<MainTopBarProps> = ({
  claimId,
  onToggleSidebar,
  onToggleMenu,
  user,
  lineOfBusiness = LINE_OF_BUSINESS.AUTO,
  claimInfo = {},
  onLogout = noop,
  breadcrumbItem,
}) => {
  const navigate = useNavigate();
  const BusinessIcon = lineOfBusiness ? LINE_OF_BUSINESS_ICON_MAP[lineOfBusiness] : null;
  const [isPanelExpanded, setIsPanelExpanded] = useState(false);

  const isClaimContext = Boolean(claimId);

  const handleToggleMenu = () => {
    setIsPanelExpanded(!isPanelExpanded);
    if (onToggleMenu) {
      onToggleMenu();
    }
  };

  const handleClaimNumberClick = () => {
    if (!claimId) {
      throw new Error('Claim ID is missing and cannot navigate to claim details.');
    }
    navigate(`/claims/${claimId}`);
  };

  return (
    <>
      <div
        className={cn('z-40 flex items-center justify-between bg-white px-12 py-4', {
          'shadow-sm': !isPanelExpanded,
        })}
      >
        <div className="flex items-center gap-12">
          <IconButton onClick={onToggleSidebar} aria-label="Toggle sidebar" round>
            <SideMenuIcon />
          </IconButton>

          <IconButton
            onClick={() => navigate('/')}
            aria-label="Home Page"
            size="md"
            className="bg-azure-200 hover:bg-azure-200 rounded-xl p-2 transition-colors duration-300 ease-in-out"
          >
            <CliveAvatar className="duration-300 hover:animate-pulse" size="lg" />
          </IconButton>

          {isClaimContext && claimId ? (
            <div className="ml-4 flex items-center gap-4 text-sm font-semibold text-blue-950">
              {BusinessIcon && (
                <span className="mr-2 inline-flex items-center">
                  <BusinessIcon size={20} className="text-grey-600" />
                </span>
              )}
              <TextButton
                className="hover:bg-bluesky-700/10 rounded-md p-8 text-black transition-colors duration-300 ease-in-out"
                onClick={handleClaimNumberClick}
              >
                {claimId}
              </TextButton>
              {breadcrumbItem && <BreadcrumbItem label={breadcrumbItem.label} action={breadcrumbItem.action} />}
            </div>
          ) : null}
        </div>

        <div className="flex items-center gap-12">
          {isClaimContext && (
            <IconButton onClick={handleToggleMenu} aria-label="Toggle Claim Information" round>
              {isPanelExpanded ? (
                <CloseIcon className="text-grey-600" />
              ) : (
                <HamburgerMenuIcon className="text-grey-600" />
              )}
            </IconButton>
          )}

          <UserMenu user={user} onLogout={onLogout} />
        </div>
      </div>

      {isClaimContext && <ClaimInfoPanel isPanelExpanded={isPanelExpanded} claimInfo={claimInfo} />}
    </>
  );
};

export default MainTopBar;
